import React, { useEffect, useState, useCallback } from "react";
import { HypeListPanel } from "@/components/HypeListPanel";
import { OrdersPanel } from "@/components/OrdersPanel";
import { AlbumBlastPanel } from "@/components/AlbumBlastPanel";
import { MyOrdersPanel } from "@/components/MyOrdersPanel";
import { DawgPanel } from "@/components/DawgPanel";
import { motion } from "framer-motion";
import { api, formatApiError } from "@/lib/wolfApi";
import { useAuth } from "@/context/AuthContext";
import WolfMark from "@/components/WolfMark";
import PackDropCalendar from "@/components/PackDropCalendar";

const reactions = [
  { key: "respect", label: "Respect", glyph: "🐺" },
  { key: "strength", label: "Strength", glyph: "🔥" },
  { key: "seen", label: "Seen", glyph: "🖤" },
];

function timeAgo(iso) {
  const d = new Date(iso);
  const s = Math.floor((Date.now() - d.getTime()) / 1000);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m`;
  if (s < 86400) return `${Math.floor(s / 3600)}h`;
  return `${Math.floor(s / 86400)}d`;
}

export default function Den() {
  const { user } = useAuth();
  const [posts, setPosts] = useState([]);
  const [content, setContent] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);

  // rollcall
  const [rc, setRc] = useState(null); // { date, today_count, checked_in, my_mood, recent }
  const [mood, setMood] = useState("");

  const loadAll = useCallback(async () => {
    try {
      const [p, r] = await Promise.all([api.get("/den/posts"), api.get("/den/rollcall")]);
      setPosts(p.data);
      setRc(r.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  }, []);

  useEffect(() => { loadAll(); }, [loadAll]);

  const submitPost = async (e) => {
    e.preventDefault();
    if (!content.trim()) return;
    setBusy(true); setErr(null);
    try {
      await api.post("/den/posts", { content: content.trim() });
      setContent("");
      await loadAll();
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setBusy(false); }
  };

  const react = async (postId, type) => {
    try {
      const r = await api.post(`/den/posts/${postId}/react`, { type });
      setPosts((prev) => prev.map((p) => (p.id === postId ? r.data : p)));
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const doRollcall = async (e) => {
    e.preventDefault();
    try {
      const r = await api.post("/den/rollcall", { mood: mood.trim() || null });
      setRc((prev) => ({ ...(prev || {}), ...r.data, my_mood: mood.trim() || null }));
      // refresh recent
      const r2 = await api.get("/den/rollcall");
      setRc(r2.data);
    } catch (e) {
      setErr(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  return (
    <div className="pt-28 sm:pt-36 pb-24 bg-[#080808] min-h-screen" data-testid="den-page">
      <div className="max-w-[1280px] mx-auto px-6 sm:px-10 lg:px-14">
        {/* HEADER */}
        <div className="flex items-start justify-between gap-6 flex-wrap">
          <div>
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-4">— The Den</p>
            <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl leading-[0.85]">The Den</h1>
            <p className="font-editorial italic text-white/55 mt-4 max-w-xl text-lg">
              A private floor. No comments. No toxic positivity. Just proof of life and three honest reactions.
            </p>
          </div>
          <div className="flex items-center gap-3 mt-4">
            <WolfMark className="w-14 h-14" />
            <div>
              <p className="text-[10px] uppercase tracking-[0.32em] text-white/40">Signed in</p>
              <p className="font-display text-2xl tracking-extreme text-white">{user?.name}</p>
            </div>
          </div>
        </div>

        {err && (
          <p className="mt-6 text-sm text-red-300 border border-red-500/20 bg-red-950/20 px-4 py-3" data-testid="den-error">
            {err}
          </p>
        )}

        <div className="mt-14 grid grid-cols-1 lg:grid-cols-12 gap-8">
          {/* LEFT: ROLLCALL */}
          <aside className="lg:col-span-4 lg:sticky lg:top-28 self-start">
            <div className="bg-[#111111] border border-white/10 p-6 sm:p-8" data-testid="rollcall-card">
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-3">— Roll Call</p>
              <h3 className="font-display text-3xl text-white">Today</h3>
              <p className="font-editorial italic text-white/55 mt-2 text-sm">
                Just say you're here. That's enough.
              </p>

              <p className="mt-6 text-[10px] uppercase tracking-[0.32em] text-white/40">
                {rc?.today_count ?? 0} of the pack checked in today
              </p>

              <form onSubmit={doRollcall} className="mt-5 flex flex-col gap-3" data-testid="rollcall-form">
                <input
                  data-testid="rollcall-mood-input"
                  value={mood}
                  onChange={(e) => setMood(e.target.value)}
                  placeholder={rc?.my_mood ? `Already in: ${rc.my_mood}` : "One word (optional): steady, hunting, rebuilding…"}
                  maxLength={40}
                  className="bg-transparent border border-white/15 px-4 py-3 text-white placeholder-white/30 text-sm focus:outline-none focus:border-[#D4AF37] transition-colors"
                />
                <button
                  type="submit"
                  data-testid="rollcall-submit-btn"
                  className="font-display tracking-[0.4em] text-sm btn-gold-fill text-black border border-[#D4AF37] px-5 py-3 transition-all"
                >
                  {rc?.checked_in ? "Update Check-In" : "I'm Here"}
                </button>
              </form>

              {rc?.recent && rc.recent.length > 0 && (
                <div className="mt-8 border-t border-white/10 pt-6">
                  <p className="text-[10px] uppercase tracking-[0.32em] text-white/40 mb-4">— On the floor</p>
                  <ul className="space-y-3" data-testid="rollcall-recent">
                    {rc.recent.slice(0, 8).map((m) => (
                      <li key={m.user_id} className="flex items-center justify-between text-sm">
                        <span className="text-white/85 font-body">{m.user_name}</span>
                        {m.mood && <span className="text-[#D4AF37]/80 font-editorial italic">{m.mood}</span>}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>

            <div className="mt-6 bg-[#111111] border border-white/10 p-6 text-sm">
              <p className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/90 mb-3">— The Code</p>
              <ul className="space-y-2 font-editorial italic text-white/65">
                <li>No comments. Reactions only.</li>
                <li>No toxic positivity. No advice unless asked.</li>
                <li>You showed up. That counts.</li>
                <li>What's said in the Den stays in the Den.</li>
              </ul>
            </div>

            <MyOrdersPanel />
            {user?.role === "admin" && <OrdersPanel />}
            {user?.role === "admin" && <AlbumBlastPanel />}
            {user?.role === "admin" && <DawgPanel />}
            {user?.role === "admin" && <HypeListPanel />}
          </aside>

          {/* RIGHT: FEED */}
          <main className="lg:col-span-8">
            {/* PACK DROP CALENDAR */}
            <PackDropCalendar />

            {/* COMPOSER */}
            <form
              onSubmit={submitPost}
              className="bg-[#111111] border border-white/10 p-6 sm:p-8"
              data-testid="post-composer"
            >
              <label className="text-[10px] uppercase tracking-[0.32em] text-[#D4AF37]/90">— Drop a thought</label>
              <textarea
                data-testid="post-content-input"
                value={content}
                onChange={(e) => setContent(e.target.value)}
                rows={3}
                maxLength={500}
                placeholder="Three sentences max. Real over polished."
                className="mt-3 w-full bg-transparent border border-white/15 px-4 py-3 text-white placeholder-white/30 focus:outline-none focus:border-[#D4AF37] transition-colors resize-none font-body"
              />
              <div className="mt-3 flex items-center justify-between">
                <span className="text-[10px] uppercase tracking-[0.28em] text-white/40">
                  {500 - content.length} chars left
                </span>
                <button
                  type="submit"
                  disabled={busy || !content.trim()}
                  data-testid="post-submit-btn"
                  className="font-display tracking-[0.4em] text-sm btn-gold-fill text-black border border-[#D4AF37] px-6 py-3 transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  {busy ? "Posting…" : "Post"}
                </button>
              </div>
            </form>

            {/* FEED */}
            <div className="mt-8 space-y-4" data-testid="den-feed">
              {posts.length === 0 && (
                <div className="border border-dashed border-white/10 p-10 text-center" data-testid="den-empty">
                  <p className="font-editorial italic text-white/50 text-lg">
                    The floor is quiet. Be the first to say you're here.
                  </p>
                </div>
              )}
              {posts.map((p, i) => (
                <motion.article
                  key={p.id}
                  initial={{ opacity: 0, y: 12 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: Math.min(i, 8) * 0.04 }}
                  className="bg-[#111111] border border-white/10 p-6 sm:p-8"
                  data-testid={`post-${p.id}`}
                >
                  <header className="flex items-center justify-between">
                    <p className="font-display tracking-extreme text-white text-lg">{p.user_name}</p>
                    <p className="text-[10px] uppercase tracking-[0.28em] text-white/35">{timeAgo(p.created_at)} ago</p>
                  </header>
                  <p className="font-body text-white/85 mt-4 leading-relaxed whitespace-pre-wrap">{p.content}</p>
                  <div className="mt-5 pt-4 border-t border-white/10 flex gap-2">
                    {reactions.map((r) => {
                      const active = p.user_reaction === r.key;
                      return (
                        <button
                          key={r.key}
                          type="button"
                          onClick={() => react(p.id, r.key)}
                          data-testid={`post-react-${r.key}-${p.id}`}
                          className={`text-[10px] uppercase tracking-[0.28em] px-3 py-2 border flex items-center gap-2 transition-all ${
                            active
                              ? "border-[#D4AF37] text-[#D4AF37] bg-[#D4AF37]/10"
                              : "border-white/15 text-white/65 hover:border-white/40 hover:text-white"
                          }`}
                          aria-pressed={active}
                        >
                          <span>{r.glyph}</span>
                          <span>{r.label}</span>
                          <span className="text-white/55">{p.reactions[r.key] || 0}</span>
                        </button>
                      );
                    })}
                  </div>
                </motion.article>
              ))}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
